import { useQuery } from '@apollo/client/react'
import { Flex, FlexProps, Heading, HStack, Icon, Text } from '@chakra-ui/react'
import { UserAvatar } from '@src/components/sco/Account/UserAvatar'
import { useParentAppLayout } from '@src/components/sco/AppLayout/AppLayoutContext'
import { AppPanelToggleButton } from '@src/components/sco/AppLayout/Panel/AppPanelToggleButton'
import { ScoThread } from '@src/graphql-types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { gql } from '@src/lib/gql'
import { LuUsers } from 'react-icons/lu'

export type ChatHeaderProps = {
  threadId?: string
  boxProps?: Partial<FlexProps>
  toggleButton?: any
}

export const ChatHeader = (props: ChatHeaderProps) => {
  const { currentUser } = useCurrentUser()
  const query = useQuery(
    gql`
      query ($threadId: String!) {
        forum {
          id
          thread(threadId: $threadId) {
            id
            title
            threadId
            threadTypeKey
            toolSession {
              id
              tool {
                id
                title
              }
            }
            userMemberships {
              id
              items {
                id
                user {
                  id
                  userId
                  fullName
                  pictureUrl
                  ${UserAvatar.fragments.ScoUser()}
                }
              }
            }
          }
        }
      }
    `,
    {
      skip: !props.threadId,
      variables: {
        threadId: props.threadId,
      },
    }
  )

  const layout = useParentAppLayout()
  const navPanel = layout.getPanel?.('*-nav')

  const thread: ScoThread = query.data?.forum.thread

  const recipientUsers = thread
    ? thread.userMemberships.items
        .filter(
          membership =>
            thread.userMemberships.items.length === 1 || membership.user.userId !== currentUser.userId
        )
        .map(membership => membership.user)
    : []

  return (
    <Flex
      alignItems={'center'}
      backdropFilter={'blur(10px)'}
      bgColor={'colorPalette.container'}
      boxShadow={'xs'}
      gap={2.5}
      height={'3.5rem'}
      minHeight={'3.5rem'}
      paddingX={3}
      zIndex={1}
      {...props.boxProps}
    >
      {props.toggleButton
        ? props.toggleButton
        : navPanel && <AppPanelToggleButton panelId={navPanel.id || '*-nav'} />}
      {props.threadId && (
        <>
          {query.error && <>{query.error.toString()}</>}
          {!thread && <Text>Loading...</Text>}
          {thread && thread.threadTypeKey === 'tool-chat' && thread.toolSession?.tool?.title && (
            <HStack align={'center'} gap={'0.75em'} justify={'flex-start'}>
              <Heading size={'sm'}>{thread.toolSession?.tool?.title}</Heading>
            </HStack>
          )}
          {thread && thread.threadTypeKey === 'direct-chat' && (
            <HStack align={'center'} gap={'0.75em'} justify={'flex-start'}>
              <HStack>
                {recipientUsers.map(user => (
                  <UserAvatar
                    key={user.userId}
                    avatarProps={{ boxShadow: 'xs', size: 'sm' }}
                    border={'none'}
                    user={user}
                  />
                ))}
              </HStack>
              <Text fontFamily={'Poppins'} fontSize={'sm'} fontWeight={'semibold'}>
                {recipientUsers.map(user => user.fullName).join(', ')}
              </Text>
            </HStack>
          )}
          {thread && thread.threadTypeKey === 'private-chat' && (
            <HStack align={'center'} justify={'flex-start'} paddingX={'1em'}>
              <Text>Private Channel</Text>
              <Text>|</Text>
              <Text fontWeight={'bold'}>{thread.title}</Text>
            </HStack>
          )}
          {thread && thread.threadTypeKey === 'public-chat' && (
            <HStack align={'center'} justify={'flex-start'} paddingX={'1em'}>
              <Icon asChild>
                <LuUsers />
              </Icon>
              <Text fontWeight={'bold'}>{thread.title}</Text>
            </HStack>
          )}
        </>
      )}
    </Flex>
  )
}
